import { Component, OnInit } from "@angular/core";
import { FormBuilder, Validators, FormGroup } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { AuthService } from "../services/auth.service";
import { CommonService } from "src/app/shared/services/common.service";
import { NavService } from "src/app/shared/services/nav.service";
import { PermissionService } from "src/app/shared/services/permission-service.service";
import { AuthCleanupService } from "src/app/shared/services/auth-clean-up.service";
import { environment } from "src/environments/environment";

@Component({
  selector: "app-login",
  templateUrl: "./login.component.html",
  styleUrls: ["./login.component.scss"],
})
export class LoginComponent implements OnInit {
  content = environment.content;
  public newUser = false;
  // public user: firebase.User;
  public loginForm: FormGroup;
  public changePasswordForm: FormGroup;
  public show: boolean = false
  public showNewPassword: boolean = false;
  public showConfirmPassword: boolean = false;
  public errorMessage: any;
  public isLoggingIn = false;
  public isChangePasswordRequired = false;
  public isChangingPassword = false;
  public shouldBlink = false;

  constructor(
    private fb: FormBuilder,
    public router: Router,
    private route: ActivatedRoute,
    private authService: AuthService,
    private commonService: CommonService,
    private navService: NavService,
    private permissionService: PermissionService,
    private authCleanupService: AuthCleanupService
  ) {
    this.loginForm = this.fb.group({
      email: ["", [Validators.required]],
      password: ["", Validators.required],
    });

    this.changePasswordForm = this.fb.group({
      userName: ["", [Validators.required]],
      oldPassword: ["", [Validators.required]],
      newPassword: ["", [Validators.required, Validators.minLength(8)]],
      confirmPassword: ["", [Validators.required]],
    }, { validator: this.passwordMatchValidator });
  }

  passwordMatchValidator(g: FormGroup) {
    return g.get('newPassword').value === g.get('confirmPassword').value
      ? null : { 'mismatch': true };
  }

  private getCompanyNames(response: any): string[] {
    const responseCompanies = Array.isArray(response?.companies) ? response.companies : [];
    if (responseCompanies.length) {
      return responseCompanies;
    }

    return Object.keys(response?.data ?? {});
  }

  private getSuccessfulCompanies(response: any): string[] {
    return this.getCompanyNames(response).filter((companyName: string) => {
      const company = response?.data?.[companyName];
      return company?.Status === true && !!company?.Token && Array.isArray(company?.data) && company.data.length > 0;
    });
  }

  private getFailedCompanyMessages(response: any): string[] {
    return Object.entries(response?.data ?? {})
      .map(([companyName, company]: [string, any]) => {
        if (company?.Status === true && company?.Token) {
          return null;
        }

        const rawMessage = company?.message || company?.error?.message || company?.exception;
        if (!rawMessage) {
          return null;
        }

        const message = String(rawMessage).split('\n')[0].trim();
        return message ? `${companyName}: ${message}` : null;
      })
      .filter((message): message is string => !!message);
  }

  private getRedirectPath(response: any): string {
    const currentCompany = localStorage.getItem('currentCompany') || this.getSuccessfulCompanies(response)[0];
    return response?.data?.[currentCompany]?.data?.[0]?.redirect_path ?? '/dashboard';
  }

  private getLoginErrorMessage(error: any): string {
    const response = error?.error;
    const nestedMessages = this.getFailedCompanyMessages(response);

    return (
      nestedMessages[0] ||
      response?.message ||
      error?.message ||
      'An error occurred during login.'
    );
  }

  ngOnInit() {
    this.authCleanupService.handleLogin();

    // Show alert when redirected due to a storage-tampering event
    this.route.queryParams.subscribe(params => {
      if (params['reason'] === 'security') {
        this.commonService.showToast(
          'warning',
          'Security Alert',
          'Your session was terminated due to a security violation.'
        );
      }
    });
  }

  async login() {
    try {
      if (this.isLoggingIn) return;
      this.isLoggingIn = true;
      if (!this.loginForm.valid) return;

      const loginData = {
        "userName": this.loginForm.value.email,
        "password": this.loginForm.value.password,
        "companyId": "UNITYCRM0021"
      };

      const res: any = await this.authService.login(loginData);

      if(res?.status === false){
        this.commonService.showToast("error", "Login Failed", res?.message || "Access denied");
        return;
      }

      const successfulCompanies = this.getSuccessfulCompanies(res);
      if (!successfulCompanies.length) {
        const failedMessages = this.getFailedCompanyMessages(res);
        this.commonService.showToast("error", "Error", failedMessages[0] || res?.message || "Login failed.");
        return;
      }

      if (res.isChangePasswordRequired) {
        // Reset the animation state
        this.shouldBlink = false;
        // Force a re-render
        setTimeout(() => {
          this.isChangePasswordRequired = true;
          // Auto-populate userName from auth service
          this.changePasswordForm.patchValue({
            userName: this.authService.getUserName()
          });
          this.shouldBlink = true;
          // Reset the animation after it completes (2 seconds)
          setTimeout(() => {
            this.shouldBlink = false;
          }, 2000);
        }, 0);
        return;
      }

      // processLoginResponse handles: token storage, AuthStateService,
      // Applications, BaseUrl, currentCompany, and integrity snapshot
      await this.authService.processLoginResponse(res);
      this.authService.getUser();
      this.authService.setLoginStatus(true);
      this.loadSideBarmenu();

      // const failedMessages = this.getFailedCompanyMessages(res);
      // if (failedMessages.length) {
      //   this.commonService.showToast("warning", "Partial Access", failedMessages.join(' | '));
      // }

      this.router.navigateByUrl(this.getRedirectPath(res));
    } catch (err) {
      const errorMessage = this.getLoginErrorMessage(err);
      this.commonService.showToast("error", "Login Error", errorMessage);
    } finally {
      this.isLoggingIn = false;
      this.commonService.stopLoader();
    }
  }

  async changePassword() {
    try {
      if (this.isChangingPassword) return;
      this.isChangingPassword = true;

      if (!this.changePasswordForm.valid) {
        this.commonService.showToast("error", "Error", "Please fill all fields correctly.");
        return;
      }

      const success = await this.authService.changePassword(this.changePasswordForm.value);
      if (success) {
        this.commonService.showToast("success", "Success", "Password changed successfully. Please login again.");
        this.isChangePasswordRequired = false;
        this.changePasswordForm.reset();
      } else {
        this.commonService.showToast("error", "Error", "Failed to change password. Please try again.");
      }
    } catch (error) {
      this.commonService.showToast("error", "Error", "An error occurred while changing password.");
    } finally {
      this.isChangingPassword = false;
    }
  }

  showPassword() {
    this.show = !this.show
  }

  showNewPasswordField() {
    this.showNewPassword = !this.showNewPassword;
  }

  showConfirmPasswordField() {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  async loadSideBarmenu() {
    await this.permissionService.updatePermission()
    // await this.navService.sideMenuInitilize();
    this.navService.getSideMenuBar();
  }




}
